import { useEffect, useRef, useState } from "react";
import { GearSix, CashRegister, Storefront, Flask, SignOut } from "@phosphor-icons/react";
import { kioskApi, errText, KIOSK_TOKEN_KEY, setDeviceMode, isKioskDevice } from "@/lib/kioskApi";

export const KIOSK_PARKED_KEY = "mm_kiosk_parked";

// Hidden owner/manager exit: hold the logo 2.5 s → PIN (checked on the server, with lockout) → choose where to go.
export function useKioskHold({ busy = false, poll = false, staffTest = null, onEndTest = null } = {}) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const enabled = isKioskDevice();
  useEffect(() => {
    if (!enabled || !poll) return undefined;
    const t = setInterval(() => kioskApi.get("/bootstrap").catch(() => {}), 30000);
    return () => clearInterval(t);
  }, [enabled, poll]);
  const stop = () => clearTimeout(timer.current);
  const bind = enabled ? {
    onPointerDown: () => { stop(); timer.current = setTimeout(() => setOpen(true), 2500); },
    onPointerUp: stop, onPointerLeave: stop, onPointerCancel: stop,
    onContextMenu: (e) => e.preventDefault(),
    style: { WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none", touchAction: "manipulation" },
  } : {};
  const overlay = open ? <KioskExit onClose={() => setOpen(false)} busy={busy} staffTest={staffTest} onEndTest={onEndTest} /> : null;
  return { bind, overlay };
}

function PinPad({ onUnlocked, onClose }) {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true); setErr("");
    try { onUnlocked((await kioskApi.post("/unlock", { pin })).data); } catch (e) { setErr(errText(e)); setPin(""); }
    setBusy(false);
  };
  return (
    <>
      <div className="text-xl font-bold font-heading text-[#0f2a3d]">Staff only</div>
      <div className="text-sm text-[#5c7385] mb-4">Owner or manager PIN.</div>
      <div className="h-12 rounded-xl bg-[#f4f1ea] flex items-center justify-center text-2xl tracking-[0.5em] mb-3" data-testid="kiosk-exit-pin-display">{"•".repeat(pin.length) || " "}</div>
      <div className="grid grid-cols-3 gap-2">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9", "C", "0", "←"].map((d) => (
          <button key={d} data-testid={`kiosk-pin-${d}`} className="h-14 rounded-2xl bg-[#eef2f5] text-xl font-semibold text-[#0f2a3d] active:scale-95 transition-transform"
            onClick={() => (d === "C" ? setPin("") : d === "←" ? setPin((p) => p.slice(0, -1)) : setPin((p) => (p + d).slice(0, 8)))}>{d}</button>
        ))}
      </div>
      {err && <div className="text-sm text-[#c2410c] mt-3" data-testid="kiosk-exit-error">{err}</div>}
      <div className="flex gap-2 mt-4">
        <button className="flex-1 h-12 rounded-full bg-[#eef2f5]" data-testid="kiosk-exit-cancel" onClick={onClose}>Cancel</button>
        <button className="flex-1 h-12 rounded-full bg-[#e2572b] text-white font-semibold disabled:opacity-40" disabled={pin.length < 4 || busy}
          data-testid="kiosk-exit-submit" onClick={submit}>Unlock</button>
      </div>
    </>
  );
}

function Choices({ unlock, busy, onClose, staffTest, onEndTest }) {
  const [err, setErr] = useState("");
  const [going, setGoing] = useState(false);
  const go = async (dest) => {
    setGoing(true); setErr("");
    try {
      const r = await kioskApi.post("/admin-open", { ticket: unlock.ticket, dest });
      localStorage.removeItem(KIOSK_TOKEN_KEY);
      localStorage.setItem("mm_token", r.data.token);
      localStorage.setItem(KIOSK_PARKED_KEY, r.data.device_id);
      setDeviceMode("normal");
      window.location.href = dest === "admin" ? "/settings/v2" : "/pos";
    } catch (e) { setErr(errText(e)); setGoing(false); }
  };
  const startTest = async () => {
    setGoing(true); setErr("");
    try { await kioskApi.post("/staff-test/start", { ticket: unlock.ticket }); window.location.href = "/kiosk"; }
    catch (e) { setErr(errText(e)); setGoing(false); }
  };
  const btn = "w-full h-14 rounded-2xl flex items-center gap-3 px-5 text-base font-semibold active:scale-[0.98] transition-transform disabled:opacity-40";
  return (
    <>
      <div className="text-xl font-bold font-heading text-[#0f2a3d]" data-testid="kiosk-exit-menu">Hi {unlock.approver}</div>
      <div className="text-sm text-[#5c7385] mb-4">Where would you like to go?</div>
      {busy && <div className="text-sm text-[#a15c00] mb-3" data-testid="kiosk-exit-busy">A payment is in progress — wait for it to finish before leaving the kiosk.</div>}
      <div className="space-y-2">
        <button className={`${btn} bg-[#0f2a3d] text-white`} disabled={busy || going} data-testid="kiosk-exit-open-admin" onClick={() => go("admin")}><GearSix size={22} /> Open Admin / Settings V2</button>
        <button className={`${btn} bg-[#eef2f5] text-[#0f2a3d]`} disabled={busy || going} data-testid="kiosk-exit-open-pos" onClick={() => go("pos")}><CashRegister size={22} /> Open Normal POS</button>
        {staffTest ? <button className={`${btn} bg-[#fff2cc] text-[#5c4300]`} disabled={going} data-testid="kiosk-exit-end-test" onClick={onEndTest}><SignOut size={22} /> End staff AI test</button>
          : <button className={`${btn} bg-[#fff2cc] text-[#5c4300]`} disabled={busy || going} data-testid="kiosk-exit-staff-test" onClick={startTest}><Flask size={22} /> Staff AI test mode</button>}
        <button className={`${btn} bg-[#e2572b] text-white`} data-testid="kiosk-exit-return" onClick={onClose}><Storefront size={22} /> Return to kiosk</button>
      </div>
      {err && <div className="text-sm text-[#c2410c] mt-3" data-testid="kiosk-exit-error">{err}</div>}
    </>
  );
}

export function KioskExit({ onClose, busy = false, staffTest = null, onEndTest = null }) {
  const [unlock, setUnlock] = useState(null);
  useEffect(() => {
    if (!unlock) return undefined;
    const t = setTimeout(onClose, 120000);
    return () => clearTimeout(t);
  }, [unlock, onClose]);
  return (
    <div className="fixed inset-0 z-[60] bg-[#0f2a3d]/85 backdrop-blur-lg flex items-center justify-center p-4" data-testid="kiosk-exit-dialog">
      <div className="bg-white rounded-[28px] w-full max-w-sm p-6 kiosk-rise">
        {unlock ? <Choices unlock={unlock} busy={busy} onClose={onClose} staffTest={staffTest} onEndTest={onEndTest} /> : <PinPad onUnlocked={setUnlock} onClose={onClose} />}
      </div>
    </div>
  );
}
